import type { Vote } from "./types";
import type { ActivityRegistrationOptionsConfig } from "../utils/activityRegistrationOptions";
import { registrationOptionsForActivityApiBody } from "../utils/activityRegistrationOptions";
import { tenantUiInstantToApiIso } from "../utils/activityCheckInDate";
import {
  type ActivityTargetAudience,
  targetAudienceToApiBody,
} from "../utils/activityTargetAudience";

type ActivityUpsert = {
  title: string
  activityType: string
  date: string
  eventEndDate?: string | null
  /** 列表/详情映射字段，与 eventEndDate 同义 */
  endDate?: string | null
  location: string
  checkInFenceLat?: number | null
  checkInFenceLng?: number | null
  checkInFenceRadiusMeters?: number | null
  checkInEarlyMinutes?: number | null
  /** null = 不限制开始后签到时限 */
  checkInLateMinutes?: number | null
  requireCheckOut?: boolean | null
  checkOutAllowEarly?: boolean | null
  checkOutEarlyMinutes?: number | null
  capacity: number
  enrolled: number
  status: string
  regStartDate?: string | null
  regEndDate?: string | null
  expenseType?: string | null
  /** 在线支付基础费用（分） */
  onlinePayAmountFen?: number | null
  /** 在线支付报名押金（分）；活动结束后符合条件自动退还 */
  onlinePayDepositAmountFen?: number | null
  /** 是否允许会员取消报名；有支付时取消将自动退款 */
  allowCancelWithRefund?: boolean | null
  cancelDeadlineMode?: string | null
  cancelDeadlineAt?: string | null
  cancelDeadlineResolvedAt?: string | null
  registrationOptions?: ActivityRegistrationOptionsConfig | null
  /** 是否允许会员代非会员亲朋好友散客登记 */
  allowMemberFriendGuestRegistration?: boolean | null
  /** 会员代登散客名额上限 */
  memberFriendGuestRegistrationQuota?: number | null
  /** 是否向普通会员展示报名人数；默认 true */
  showEnrollmentCountToMembers?: boolean | null
  /** 是否在活动报名页展示微信分享按钮；默认 true */
  showShareOnRegistrationPage?: boolean | null
  targetAudience?: ActivityTargetAudience | null
  linkedAssociations?: Array<{ tenantId: string; quota: number }> | null
  description?: string | null
  tags?: string | null
  participationPoints?: number | null
  enableParticipationPointDeduction?: boolean | null
  notRegisteredDeductionPoints?: number | null
  notCheckedInDeductionPoints?: number | null
  coverUrl?: string | null
  requireDocSign?: boolean | null
  requiredDocIds?: string[] | null
  activitySummaryJson?: string | null
  rejectionNote?: string | null
  approvedAt?: string | null
  ageMin?: number | null
  ageMax?: number | null
}

function datetimeLocalValueToIso(local: string): string | null {
  const t = local.trim()
  if (!t) return null
  const d = new Date(t)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

function activityOrVoteInstantToApiIso(raw: string): string {
  const t = raw.trim()
  if (!t) return t
  if (/^\d{4}-\d{2}-\d{2}$/.test(t) || /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}$/.test(t)) {
    return tenantUiInstantToApiIso(t)
  }
  return datetimeLocalValueToIso(t) ?? tenantUiInstantToApiIso(t)
}

function optionalActivityOrVoteInstantToApiIso(raw: string | null | undefined): string | null {
  const t = String(raw ?? '').trim()
  if (!t) return null
  return activityOrVoteInstantToApiIso(t)
}

function resolveActivityEventEndSource(a: ActivityUpsert): string {
  const direct = String(a.eventEndDate ?? '').trim()
  if (direct) return direct
  return String(a.endDate ?? '').trim()
}

function normalizeCheckInEarlyMinutes(raw: number | null | undefined): number {
  const n = Number(raw ?? 0)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.min(Math.floor(n), 7 * 24 * 60)
}

/** 与后端 CreateActivityBody / UpdateActivityBody 对齐（camelCase JSON） */
export function activityToApiBody(a: ActivityUpsert): Record<string, unknown> {
  return {
    title: a.title,
    activityType: a.activityType,
    date: activityOrVoteInstantToApiIso(a.date),
    location: a.location || '待定',
    checkInFenceLat: a.checkInFenceLat ?? null,
    checkInFenceLng: a.checkInFenceLng ?? null,
    checkInFenceRadiusMeters: a.checkInFenceRadiusMeters ?? null,
    checkInEarlyMinutes: normalizeCheckInEarlyMinutes(a.checkInEarlyMinutes),
    checkInLateMinutes:
      a.checkInLateMinutes != null && Number.isFinite(Number(a.checkInLateMinutes))
        ? Math.max(0, Math.min(7 * 24 * 60, Math.floor(Number(a.checkInLateMinutes))))
        : null,
    requireCheckOut: Boolean(a.requireCheckOut),
    checkOutAllowEarly: Boolean(a.checkOutAllowEarly),
    checkOutEarlyMinutes: normalizeCheckInEarlyMinutes(a.checkOutEarlyMinutes),
    capacity: a.capacity,
    enrolled: a.enrolled,
    status: a.status,
    regStartDate: optionalActivityOrVoteInstantToApiIso(a.regStartDate),
    regEndDate: optionalActivityOrVoteInstantToApiIso(a.regEndDate),
    expenseType: a.expenseType ?? '',
    onlinePayAmountFen:
      (a.expenseType ?? '').trim() === '在线支付'
        ? Math.max(0, Math.round(Number(a.onlinePayAmountFen ?? 0)))
        : 0,
    onlinePayDepositAmountFen:
      (a.expenseType ?? '').trim() === '在线支付'
        ? Math.max(0, Math.round(Number(a.onlinePayDepositAmountFen ?? 0)))
        : 0,
    allowCancelWithRefund: Boolean(a.allowCancelWithRefund),
    cancelDeadlineMode: String(a.cancelDeadlineMode ?? 'reg_end').trim() || 'reg_end',
    cancelDeadlineAt: optionalActivityOrVoteInstantToApiIso(a.cancelDeadlineAt),
    registrationOptions: registrationOptionsForActivityApiBody(a.registrationOptions),
    allowMemberFriendGuestRegistration: Boolean(a.allowMemberFriendGuestRegistration),
    memberFriendGuestRegistrationQuota: a.allowMemberFriendGuestRegistration
      ? Math.max(0, Math.min(99999, Math.floor(Number(a.memberFriendGuestRegistrationQuota ?? 0))))
      : 0,
    showEnrollmentCountToMembers: a.showEnrollmentCountToMembers !== false,
    showShareOnRegistrationPage: a.showShareOnRegistrationPage !== false,
    targetAudience: targetAudienceToApiBody(
      a.targetAudience ?? { levels: ['所有等级'], groups: ['所有分组'] },
    ),
    linkedAssociations: Array.isArray(a.linkedAssociations)
      ? a.linkedAssociations
          .filter((x) => x && String(x.tenantId || '').trim())
          .map((x) => ({
            tenantId: String(x.tenantId).trim(),
            quota: Math.max(0, Math.min(99999, Math.floor(Number(x.quota) || 0))),
          }))
      : [],
    description: a.description ?? null,
    tags: a.tags ?? null,
    participationPoints: a.participationPoints ?? null,
    enableParticipationPointDeduction: Boolean(a.enableParticipationPointDeduction),
    notRegisteredDeductionPoints: Math.max(0, Math.min(99999, Math.floor(Number(a.notRegisteredDeductionPoints ?? 0)))),
    notCheckedInDeductionPoints: Math.max(0, Math.min(99999, Math.floor(Number(a.notCheckedInDeductionPoints ?? 0)))),
    coverUrl: a.coverUrl ?? null,
    requireDocSign: a.requireDocSign ?? false,
    requiredDocIds: a.requiredDocIds?.length ? a.requiredDocIds : null,
    activitySummaryJson: a.activitySummaryJson ?? null,
    rejectionNote: a.rejectionNote ?? null,
    approvedAt: a.approvedAt ?? null,
    ageMin: a.ageMin ?? null,
    ageMax: a.ageMax ?? null,
    eventEndDate: optionalActivityOrVoteInstantToApiIso(resolveActivityEventEndSource(a)),
  }
}

/** 与后端 CreateVoteBody / UpdateVoteBody 对齐（camelCase JSON） */
export function voteToUpsertBody(v: Vote): Record<string, unknown> {
  const scope = (v as any).targetScope as
    | { type: string; activityId?: string | null; activityTitle?: string | null }
    | undefined

  return {
    title: v.title,
    description: v.description ?? '',
    status: v.status,
    startDate: activityOrVoteInstantToApiIso(v.startDate),
    endDate: activityOrVoteInstantToApiIso(v.endDate),
    totalVoters: v.totalVoters ?? 0,
    allowMultiple: v.allowMultiple,
    isAnonymous: v.isAnonymous,
    showToMembers: v.showToMembers,
    scheduledPublishAt: v.scheduledPublishAt ?? null,
    targetScope: scope ?? { type: 'all', activityId: null, activityTitle: null },
    options: (v.options ?? []).map((o, idx) => ({
      legacyOptionId: o.id,
      text: o.text,
      votes: o.votes ?? 0,
      imageUrl: o.imageUrl ?? null,
      description: o.description ?? null,
      sortOrder: idx,
    })),
  }
}
